import * as Yup from 'yup';

// Common validation rules
export const commonValidationRules = {
  email: Yup.string().email('Invalid email').required('Please enter your email'),

  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Please enter your password'),

  confirmPassword: (passwordField: string = 'password') =>
    Yup.string()
      .oneOf([Yup.ref(passwordField)], 'Passwords do not match')
      .required('Please confirm your password'),

  required: (fieldName: string) => Yup.string().required(`Please enter ${fieldName}`),

  phoneNumber: Yup.string()
    .matches(/^[0-9+\-\s()]+$/, 'Invalid phone number')
    .min(10, 'Phone number must have at least 10 digits')
    .required('Please enter your phone number'),

  name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters')
    .required('Please enter your name'),

  username: Yup.string()
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Username cannot exceed 20 characters')
    .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .required('Please enter your username'),

  url: Yup.string().url('Invalid URL').required('Please enter a URL'),

  number: (fieldName: string) =>
    Yup.number().typeError(`${fieldName} must be a number`).required(`Please enter ${fieldName}`),

  positiveNumber: (fieldName: string) =>
    Yup.number()
      .typeError(`${fieldName} must be a number`)
      .positive(`${fieldName} must be a positive number`)
      .required(`Please enter ${fieldName}`),

  date: Yup.date().typeError('Invalid date').required('Please select a date'),

  boolean: Yup.boolean(),
};

// Specific validation schemas
export const validationSchemas = {
  // Login form
  login: Yup.object({
    email: commonValidationRules.email,
    password: commonValidationRules.password,
    rememberMe: commonValidationRules.boolean,
  }),

  register: Yup.object({
    displayName: Yup.string()
      .trim()
      .min(2, 'Display name must be at least 2 characters')
      .max(50, 'Display name cannot exceed 50 characters')
      .required('Please enter your display name'),
    email: commonValidationRules.email,
    phoneNumber: Yup.string()
      .trim()
      .matches(/^[0-9+\-\s()]+$/, 'Invalid phone number')
      .test('phone-digits', 'Phone number must contain 10 to 15 digits', (value) => {
        if (!value) return false;
        const digitCount = value.replace(/\D/g, '').length;
        return digitCount >= 10 && digitCount <= 15;
      })
      .required('Please enter your phone number'),
    password: commonValidationRules.password,
    confirmPassword: commonValidationRules.confirmPassword('password'),
    referralCode: Yup.string().trim().optional(),
  }),

  // Invite Register form
  inviteRegister: Yup.object({
    password: commonValidationRules.password,
    confirmPassword: commonValidationRules.confirmPassword('password'),
    inviteCode: Yup.string().required('Invite code is required'),
    agreeUserAgreement: Yup.boolean().oneOf([true], 'You must agree to the User Agreement'),
    agreePrivacyPolicy: Yup.boolean().oneOf([true], 'You must agree to the Privacy Policy'),
    agreeElectronicDisclosures: Yup.boolean().oneOf(
      [true],
      'You must agree to the Electronic Disclosures',
    ),
  }),

  // Forgot password
  forgotPassword: Yup.object({
    email: commonValidationRules.email,
  }),

  // Reset password
  resetPassword: Yup.object({
    password: commonValidationRules.password,
    confirmPassword: commonValidationRules.confirmPassword('password'),
  }),

  // Change password
  changePassword: Yup.object({
    oldPassword: Yup.string().required('Please enter your current password'),
    newPassword: Yup.string()
      .min(8, 'New password must be at least 8 characters')
      .required('Please enter a new password'),
    newPasswordConfirmation: commonValidationRules.confirmPassword('newPassword'),
  }),

  // User profile
  userProfile: Yup.object({
    name: commonValidationRules.name,
    email: commonValidationRules.email,
    phone: commonValidationRules.phoneNumber,
    address: Yup.string().max(200, 'Address cannot exceed 200 characters'),
    bio: Yup.string().max(500, 'Bio cannot exceed 500 characters'),
  }),

  // Settings
  settings: Yup.object({
    siteName: commonValidationRules.required('site name'),
    siteUrl: commonValidationRules.url,
    adminEmail: commonValidationRules.email,
    supportEmail: commonValidationRules.email,
  }),

  // Product form
  product: Yup.object({
    name: commonValidationRules.required('product name'),
    description: Yup.string().max(1000, 'Description cannot exceed 1000 characters'),
    price: commonValidationRules.positiveNumber('price'),
    category: commonValidationRules.required('category'),
    stock: commonValidationRules.number('stock quantity'),
  }),

  // Contact form
  contact: Yup.object({
    name: commonValidationRules.name,
    email: commonValidationRules.email,
    subject: commonValidationRules.required('subject'),
    message: Yup.string()
      .min(10, 'Message must be at least 10 characters')
      .max(1000, 'Message cannot exceed 1000 characters')
      .required('Please enter your message'),
  }),

  // Invite user form
  inviteUser: Yup.object({
    email: commonValidationRules.email,
    firstName: commonValidationRules.name,
    lastName: commonValidationRules.name,
    role: Yup.string().required('Please select a role'),
  }),

  // Edit user form
  editUser: Yup.object({
    email: Yup.string()
      .transform((value) => (typeof value === 'string' ? value.trim() : value))
      .email('Invalid email format')
      .required('Please enter your email')
      .test('no-spaces', 'Email cannot contain spaces', (value) => {
        if (!value) return true;
        return !value.includes(' ');
      }),
    firstName: commonValidationRules.name,
    lastName: commonValidationRules.name,
    role: Yup.number()
      .min(1, 'Please select a role')
      .max(2, 'Invalid role')
      .required('Please select a role'),
  }),
};

// Helper functions
export const createCustomSchema = (fields: Record<string, any>) => {
  return Yup.object(fields);
};

export const validateField = async (value: any, schema: Yup.Schema) => {
  try {
    await schema.validate(value);
    return { isValid: true, error: null };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Yup.ValidationError ? error.message : 'Validation error',
    };
  }
};

export const validateForm = async (values: any, schema: Yup.Schema) => {
  try {
    await schema.validate(values, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      const errors: Record<string, string> = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return { isValid: false, errors };
    }
    return { isValid: false, errors: { general: 'Validation error' } };
  }
};
